import { z } from 'zod';

export const playerSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  nickname: z.string().nullable(),
  photoUrl: z.string().nullable(),
  telegramUserId: z.number().nullable(),
  /** Привязан ли Telegram (app.telegram_links); без него — гейт привязки. */
  telegramLinked: z.boolean(),
  termsAccepted: z.boolean(),
  onboardingCompleted: z.boolean(),
  isNew: z.boolean(),
});
export type Player = z.infer<typeof playerSchema>;

export const otpRequestSchema = z.object({ devCode: z.string().nullable() });

export const notificationPrefsSchema = z.object({
  push: z.boolean(),
  tournaments: z.boolean(),
  rating: z.boolean(),
});
export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>;

export const authResponseSchema = z.object({
  token: z.string(),
  player: playerSchema,
});

export const seasonSchema = z.object({
  id: z.number(),
  name: z.string(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  finaleAt: z.string().nullable(),
  isActive: z.boolean(),
  tournaments: z.number(),
  players: z.number(),
});
export type Season = z.infer<typeof seasonSchema>;

export const leaderboardRowSchema = z.object({
  rank: z.number(),
  playerId: z.number(),
  name: z.string(),
  points: z.number(),
  wins: z.number(),
  gamesPlayed: z.number(),
  isMe: z.boolean(),
});
export type LeaderboardRow = z.infer<typeof leaderboardRowSchema>;

export const leaderboardSchema = z.object({
  seasonId: z.number().nullable(),
  rows: z.array(leaderboardRowSchema),
  me: leaderboardRowSchema.nullable(),
});
export type Leaderboard = z.infer<typeof leaderboardSchema>;

export const recentTournamentSchema = z.object({
  gameId: z.number(),
  startTime: z.string(),
  title: z.string().nullable(),
  format: z.string().nullable(),
  position: z.number().nullable(),
  points: z.number(),
});

export const myTournamentSchema = recentTournamentSchema.extend({
  fieldSize: z.number(),
  seasonId: z.number().nullable(),
  /** Позиционное «в очках» (место в призовой зоне; баунти-очки не считаются). */
  itm: z.boolean().optional().default(false),
  /** Рейтинговая игра; false у финалов/спец-турниров — вне знаменателя «В очках %». */
  rated: z.boolean().optional().default(false),
});
export type MyTournament = z.infer<typeof myTournamentSchema>;

export const tournamentResultSchema = z.object({
  game: z.object({
    id: z.number(),
    startTime: z.string(),
    title: z.string().nullable(),
    format: z.string().nullable(),
    seasonId: z.number().nullable(),
    fieldSize: z.number(),
  }),
  rows: z.array(
    z.object({
      position: z.number().nullable(),
      playerId: z.number(),
      name: z.string(),
      points: z.number(),
      /** Разбивка очков (место/баунти); null — игра ещё не пересчитана движком. */
      placePoints: z.number().nullable().optional(),
      bountyPoints: z.number().nullable().optional(),
      isMe: z.boolean(),
    }),
  ),
});
export type TournamentResult = z.infer<typeof tournamentResultSchema>;

export const profileSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  nickname: z.string().nullable(),
  email: z.string().nullable(),
  photoUrl: z.string().nullable(),
  /** Привязан ли Telegram — без привязки лестница гейтов не пустит дальше. */
  telegramLinked: z.boolean(),
  termsAccepted: z.boolean(),
  onboardingCompleted: z.boolean(),
  notifications: notificationPrefsSchema,
  seasonId: z.number().nullable(),
  stats: z.object({
    points: z.number(),
    rank: z.number().nullable(),
    wins: z.number(),
    gamesPlayed: z.number(),
    itmPercent: z.number(),
  }),
  recent: z.array(recentTournamentSchema),
});
export type Profile = z.infer<typeof profileSchema>;

export const publicProfileSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  photoUrl: z.string().nullable(),
  seasonId: z.number().nullable(),
  stats: z.object({
    points: z.number(),
    rank: z.number().nullable(),
    wins: z.number(),
    gamesPlayed: z.number(),
    itmPercent: z.number(),
  }),
  recent: z.array(recentTournamentSchema),
});
export type PublicProfile = z.infer<typeof publicProfileSchema>;

export const livePlayerSchema = z.object({
  playerId: z.number().nullable(),
  name: z.string(),
  photoUrl: z.string().nullable(),
  status: z.string(),
  stack: z.number(),
  tableNo: z.number().nullable(),
  rebuys: z.number(),
  finalPosition: z.number().nullable(),
  knockouts: z.number(),
  /** Provisional rating points earned so far (place points + bounty). */
  points: z.number(),
  isMe: z.boolean(),
});
export type LivePlayer = z.infer<typeof livePlayerSchema>;

export const liveSchema = z.object({
  live: z.boolean(),
  status: z.string(),
  updatedAt: z.string().nullable(),
  level: z.number(),
  smallBlind: z.number(),
  bigBlind: z.number(),
  ante: z.number(),
  isBreak: z.boolean(),
  isPaused: z.boolean(),
  levelEndsAt: z.string().nullable(),
  timeLeftSec: z.number(),
  /** Late registration: open until the END of the flagged blind level in Korin. */
  lateRegOpen: z.boolean(),
  lateRegTimeLeftSec: z.number(),
  lateRegEndsAt: z.string().nullable(),
  lateRegLevel: z.number().nullable(),
  /** Arrived players (IN_GAME + OUT), like Korin's display. */
  playersTotal: z.number(),
  playersIn: z.number(),
  totalRebuys: z.number(),
  knockouts: z.number(),
  startingStack: z.number(),
  avgStack: z.number(),
  chipsInPlay: z.number(),
  breakInSec: z.number(),
  breakEndsAt: z.string().nullable(),
  nextSmallBlind: z.number(),
  nextBigBlind: z.number(),
  /** Сетка призовых мест (место → провизорные очки); default([]) — совместимость
   *  со старыми кэшами/пейлоадами без поля. */
  prizes: z.array(z.object({ place: z.number(), points: z.number() })).default([]),
  players: z.array(livePlayerSchema),
});
export type Live = z.infer<typeof liveSchema>;

export const appStatusSchema = z.object({
  maintenance: z.boolean(),
  message: z.string().nullable(),
});
export type AppStatus = z.infer<typeof appStatusSchema>;

export const avatarUploadSchema = z.object({ photoUrl: z.string() });

export const tournamentSchema = z.object({
  id: z.number(),
  startTime: z.string(),
  dayOfWeek: z.string().nullable(),
  title: z.string().nullable(),
  format: z.string().nullable(),
  buyIn: z.number(),
  guarantee: z.number().nullable(),
  startingStack: z.number().nullable(),
  blindMinutes: z.number().nullable(),
  description: z.string().nullable(),
  status: z.string().nullable(),
  seasonId: z.number().nullable(),
  capacity: z.number().nullable(),
  registrationOpen: z.boolean(),
  registeredCount: z.number(),
  /** Вылетевшие в идущей игре: их кресла свободны; optional — совместимость
   *  со старым API до деплоя (эквивалент 0). */
  bustedCount: z.number().optional(),
  registeredByMe: z.boolean(),
  waitlistCount: z.number(),
  myWaitlisted: z.boolean(),
  myWaitlistPosition: z.number().nullable(),
  /** Past-list extras (T1 cards); null for upcoming tournaments. */
  playersCount: z.number().nullable(),
  winner: z.string().nullable(),
  myPlace: z.number().nullable(),
  myPoints: z.number().nullable(),
});
export type Tournament = z.infer<typeof tournamentSchema>;

export const registrationSchema = z.object({
  playerId: z.number(),
  nickname: z.string(),
  photoUrl: z.string().nullable(),
  registeredAt: z.string(),
  rank: z.number().nullable(),
  waitlisted: z.boolean(),
});
export type Registration = z.infer<typeof registrationSchema>;

export const registrationsSchema = z.object({
  players: z.array(registrationSchema),
  total: z.number(),
});
export type Registrations = z.infer<typeof registrationsSchema>;

export const newsSchema = z.object({
  id: z.number(),
  title: z.string(),
  body: z.string().nullable(),
  icon: z.string().nullable(),
  imageUrl: z.string().nullable(),
  isPinned: z.boolean(),
  publishedAt: z.string(),
});
export type NewsItem = z.infer<typeof newsSchema>;

export const homeSchema = z.object({
  nextTournament: tournamentSchema.nullable(),
  news: z.array(newsSchema),
});
export type Home = z.infer<typeof homeSchema>;

/** FAQ: вопрос-ответ, контент редактируется в CRM (только published). */
export const faqItemSchema = z.object({
  id: z.number(),
  question: z.string(),
  answer: z.string(),
});
export const faqListSchema = z.array(faqItemSchema);
export type FaqItem = z.infer<typeof faqItemSchema>;
